import React, { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { X, ExternalLink } from 'lucide-react';
import { portal } from '../api';
import type { BillingCycle, Product } from '../types';

export default function ConfigureModal({
  product,
  onClose,
}: {
  product: Product;
  onClose: () => void;
}) {
  const [planId, setPlanId] = useState(product.plans[0]?.id ?? '');
  const [billingCycle, setBillingCycle] = useState<BillingCycle>('monthly');
  const [config, setConfig] = useState<Record<string, string>>(() =>
    Object.fromEntries(product.configFields.map((f) => [f.key, f.defaultValue ?? ''])),
  );
  const [error, setError] = useState('');

  const checkoutMutation = useMutation({
    mutationFn: () =>
      portal.checkout({ productId: product.id, planId, billingCycle, configuration: config }),
    onSuccess: ({ checkoutUrl }) => {
      // Hand the customer straight to the pre-filled WooCommerce checkout
      window.location.href = checkoutUrl;
    },
    onError: (err: unknown) => {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        'Weiterleitung zum Checkout fehlgeschlagen.';
      setError(msg);
    },
  });

  const selectedPlan = product.plans.find((p) => p.id === planId);
  const price =
    billingCycle === 'yearly' ? selectedPlan?.yearlyPrice : selectedPlan?.monthlyPrice;

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-surface rounded-xl shadow-xl w-full max-w-lg max-h-screen overflow-y-auto">
        <div className="px-6 py-5 border-b border-slate-200 sticky top-0 bg-surface flex items-start justify-between">
          <div>
            <h2 className="text-base font-semibold text-slate-900">{product.name} konfigurieren</h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Nach dem Bestätigen geht es direkt zur Kasse.
            </p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X size={18} />
          </button>
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            setError('');
            checkoutMutation.mutate();
          }}
          className="px-6 py-5 space-y-5"
        >
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Tarif</label>
            <div className="space-y-2">
              {product.plans.map((plan) => (
                <label
                  key={plan.id}
                  className={`flex items-center justify-between border rounded-lg px-3 py-2.5 cursor-pointer transition-colors ${
                    planId === plan.id
                      ? 'border-brand-500 bg-brand-50'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <div className="flex items-center gap-2.5">
                    <input
                      type="radio"
                      name="plan"
                      value={plan.id}
                      checked={planId === plan.id}
                      onChange={() => setPlanId(plan.id)}
                      className="accent-brand-600"
                    />
                    <span className="text-sm font-medium text-slate-900">{plan.name}</span>
                  </div>
                  <span className="text-sm text-slate-500">
                    {plan.monthlyPrice && <>€{plan.monthlyPrice}/Mon.</>}
                    {plan.yearlyPrice && <> · €{plan.yearlyPrice}/Jahr</>}
                  </span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Abrechnung</label>
            <div className="flex gap-2">
              {(['monthly', 'yearly'] as BillingCycle[]).map((cycle) => {
                const available =
                  cycle === 'yearly' ? selectedPlan?.yearlyPrice : selectedPlan?.monthlyPrice;
                return (
                  <button
                    key={cycle}
                    type="button"
                    disabled={!available}
                    onClick={() => setBillingCycle(cycle)}
                    className={`flex-1 text-sm py-2 rounded-lg border transition-colors disabled:opacity-40 ${
                      billingCycle === cycle
                        ? 'border-brand-500 bg-brand-50 text-brand-800 font-medium'
                        : 'border-slate-200 text-slate-600 hover:border-slate-300'
                    }`}
                  >
                    {cycle === 'monthly' ? 'Monatlich' : 'Jährlich'}
                  </button>
                );
              })}
            </div>
          </div>

          {product.configFields.length > 0 && (
            <div className="space-y-3">
              <label className="block text-sm font-medium text-slate-700">Konfiguration</label>
              {product.configFields.map((field) => (
                <div key={field.id}>
                  <label className="block text-xs text-slate-500 mb-1">
                    {field.label}
                    {field.required && <span className="text-red-500"> *</span>}
                  </label>
                  <input
                    required={field.required}
                    value={config[field.key] ?? ''}
                    onChange={(e) =>
                      setConfig((c) => ({ ...c, [field.key]: e.target.value }))
                    }
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                  />
                </div>
              ))}
            </div>
          )}

          <div className="bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 flex items-center justify-between">
            <span className="text-sm text-slate-600">Gesamt</span>
            <span className="text-lg font-semibold text-slate-900">
              {price ? `€${price}` : '—'}
              <span className="text-xs font-normal text-slate-500 ml-1">
                / {billingCycle === 'yearly' ? 'Jahr' : 'Monat'}
              </span>
            </span>
          </div>

          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm border border-slate-300 rounded-lg text-slate-600"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={checkoutMutation.isPending || !planId || !price}
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm bg-brand-600 hover:bg-brand-700 text-white font-medium rounded-lg disabled:opacity-60"
            >
              {checkoutMutation.isPending ? 'Weiterleitung…' : 'Zur Kasse'}
              <ExternalLink size={14} />
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
